/**
 * Door types for the Visual Designer: ids, the shared door test, per-type fields and starting drawings.
 * Pure module with no imports, because scripts/sync-edge-shared.js copies it into supabase/functions/_shared.
 * Left and right for master leaf, stack and traffic door are as viewed from outside.
 */

export const SINGLE_DOOR_TYPE_ID = 'upvc-door';
export const DOOR_AND_WINDOW_TYPE_ID = 'door-window';
export const FRENCH_DOORS_TYPE_ID = 'french-doors';
export const BIFOLD_DOORS_TYPE_ID = 'bifold-doors';
export const SLIDING_DOORS_TYPE_ID = 'sliding-doors';

/** Door types added after the single Door; these ids count as doors everywhere. */
export const MULTI_LEAF_DOOR_TYPE_IDS = [FRENCH_DOORS_TYPE_ID, BIFOLD_DOORS_TYPE_ID, SLIDING_DOORS_TYPE_ID] as const;

export const DOOR_TYPE_LABELS: Record<string, string> = {
  [SINGLE_DOOR_TYPE_ID]: 'Door',
  [FRENCH_DOORS_TYPE_ID]: 'French Doors',
  [BIFOLD_DOORS_TYPE_ID]: 'Bi-Folds',
  [SLIDING_DOORS_TYPE_ID]: 'Sliders',
  [DOOR_AND_WINDOW_TYPE_ID]: 'Door & Window',
};

export type DoorKind = 'single' | 'french' | 'bifold' | 'slider' | 'door_window';

type WindowTypeLike = { id?: unknown; name?: unknown } | null | undefined;

const normalizeTypeId = (windowType: WindowTypeLike): string => String(windowType?.id || '').trim().toLowerCase();

/** Door types drawn with door handles throughout. Door & Window is not one of them. */
export function isDoorOnlyTypeId(typeId: unknown): boolean {
  const id = String(typeId ?? '').trim().toLowerCase();
  return id === SINGLE_DOOR_TYPE_ID || (MULTI_LEAF_DOOR_TYPE_IDS as readonly string[]).includes(id);
}

/** True for upvc-door, the new door type ids, or a type name containing the word "door". */
export function isDoorType(windowType: WindowTypeLike): boolean {
  const id = normalizeTypeId(windowType);
  const name = String(windowType?.name || '').trim();
  return id === SINGLE_DOOR_TYPE_ID || (MULTI_LEAF_DOOR_TYPE_IDS as readonly string[]).includes(id) || /\bdoor\b/i.test(name);
}

/** Which kind of door an item is, or null for windows. Unknown door names count as a single door. */
export function getDoorKind(windowType: WindowTypeLike): DoorKind | null {
  if (!isDoorType(windowType)) return null;
  const id = normalizeTypeId(windowType);
  if (id === FRENCH_DOORS_TYPE_ID) return 'french';
  if (id === BIFOLD_DOORS_TYPE_ID) return 'bifold';
  if (id === SLIDING_DOORS_TYPE_ID) return 'slider';
  if (id === DOOR_AND_WINDOW_TYPE_ID || /\bdoor\s*&\s*window\b/i.test(String(windowType?.name || ''))) return 'door_window';
  return 'single';
}

/** Door kinds that save hinge placement (Left / Right). */
export const doorKindUsesHingePlacement = (kind: DoorKind | null): boolean => kind === 'single' || kind === 'door_window';
/** Door kinds that save open direction (In / Out). */
export const doorKindUsesOpenDirection = (kind: DoorKind | null): boolean => !!kind && kind !== 'slider';

export type DoorSideCode = 'left' | 'right';
export type StackDirectionCode = 'left' | 'right' | 'split';
export type TrafficDoorCode = 'none' | 'left' | 'right';
export type ThresholdCode = 'standard' | 'low-aluminium' | 'low-upvc' | 'mobility-mds25';

export const MASTER_LEAF_OPTIONS: Array<{ code: DoorSideCode; label: string }> = [
  { code: 'left', label: 'Left' },
  { code: 'right', label: 'Right' },
];
export const STACK_DIRECTION_OPTIONS: Array<{ code: StackDirectionCode; label: string }> = [
  { code: 'left', label: 'Left' },
  { code: 'right', label: 'Right' },
  { code: 'split', label: 'Split' },
];
export const TRAFFIC_DOOR_OPTIONS: Array<{ code: TrafficDoorCode; label: string }> = [
  { code: 'none', label: 'None' },
  { code: 'left', label: 'Left' },
  { code: 'right', label: 'Right' },
];
/** Every door type, including Door & Window. No effect on the drawing. */
export const THRESHOLD_OPTIONS: Array<{ code: ThresholdCode; label: string }> = [
  { code: 'standard', label: 'Standard' },
  { code: 'low-aluminium', label: 'Low Aluminium' },
  { code: 'low-upvc', label: 'Low uPVC' },
  { code: 'mobility-mds25', label: 'Mobility MDS25' },
];

export const BIFOLD_PANEL_COUNT_RANGE = { min: 2, max: 7, default: 3 } as const;
export const SLIDER_PANEL_COUNT_RANGE = { min: 2, max: 4, default: 2 } as const;

export const DEFAULT_MASTER_LEAF: DoorSideCode = 'right';
export const DEFAULT_STACK_DIRECTION: StackDirectionCode = 'left';
export const DEFAULT_TRAFFIC_DOOR: TrafficDoorCode = 'left';
export const DEFAULT_THRESHOLD: ThresholdCode = 'standard';

export const clampPanelCount = (kind: 'bifold' | 'slider', value: unknown): number => {
  const range = kind === 'bifold' ? BIFOLD_PANEL_COUNT_RANGE : SLIDER_PANEL_COUNT_RANGE;
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return range.default;
  return Math.min(range.max, Math.max(range.min, n));
};

const labelFor = <T extends string>(options: Array<{ code: T; label: string }>, raw: unknown): string => {
  const code = String(raw || '').trim().toLowerCase();
  return options.find((o) => o.code === code)?.label || '';
};
export const formatMasterLeaf = (raw: unknown): string => labelFor(MASTER_LEAF_OPTIONS, raw);
export const formatStackDirection = (raw: unknown): string => labelFor(STACK_DIRECTION_OPTIONS, raw);
export const formatTrafficDoor = (raw: unknown): string => labelFor(TRAFFIC_DOOR_OPTIONS, raw);
export const formatThreshold = (raw: unknown): string => labelFor(THRESHOLD_OPTIONS, raw);
export const parseThresholdCode = (raw: unknown): ThresholdCode => {
  const code = String(raw || '').trim().toLowerCase();
  return THRESHOLD_OPTIONS.find((o) => o.code === code)?.code || DEFAULT_THRESHOLD;
};

type LeafBox = { id: string; x: number; y: number; w: number; h: number };

function collectLeafBoxes(node: any, box: Omit<LeafBox, 'id'>, out: LeafBox[]): void {
  if (!node || typeof node !== 'object') return;
  if (node.type === 'rectUnion' && Array.isArray(node.regions)) {
    for (const rg of node.regions) {
      collectLeafBoxes(rg?.root, {
        x: box.x + (Number(rg?.x) || 0) * box.w,
        y: box.y + (Number(rg?.y) || 0) * box.h,
        w: (Number(rg?.w) || 0) * box.w,
        h: (Number(rg?.h) || 0) * box.h,
      }, out);
    }
    return;
  }
  const children: any[] = Array.isArray(node.children) ? node.children : [];
  if (node.split === 'none' || !children.length) {
    if (node.id != null) out.push({ id: String(node.id), ...box });
    return;
  }
  const fractions: number[] = Array.isArray(node.fractions) && node.fractions.length === children.length
    ? node.fractions.map((f: unknown) => Number(f) || 0)
    : children.map(() => 1 / children.length);
  let offset = 0;
  children.forEach((child, i) => {
    const f = fractions[i];
    const childBox = node.split === 'vertical'
      ? { x: box.x + offset * box.w, y: box.y, w: f * box.w, h: box.h }
      : { x: box.x, y: box.y + offset * box.h, w: box.w, h: f * box.h };
    offset += f;
    collectLeafBoxes(child, childBox, out);
  });
}

/** Leaf ids in Window Generator schedule order (top to bottom, then left to right): A1, A2, ... */
export function getScheduleOrderedLeafIds(payload: any): string[] {
  const out: LeafBox[] = [];
  collectLeafBoxes(payload?.root, { x: 0, y: 0, w: 1, h: 1 }, out);
  const rowTol = 0.01;
  out.sort((a, b) => (Math.abs(a.y - b.y) < rowTol ? a.x - b.x : a.y - b.y));
  return out.map((l) => l.id);
}

const SLIDE_WORDING: Record<string, string> = {
  'slide-left': 'slides left',
  'slide-right': 'slides right',
  'slide-both': 'slides both ways',
};

/** Which panels slide, read from the drawing, e.g. "A2 slides left". Empty when none slide. */
export function describeSlidingPanels(payload: any): string {
  const slides = (payload?.slideOpens && typeof payload.slideOpens === 'object') ? payload.slideOpens : {};
  return getScheduleOrderedLeafIds(payload)
    .map((id, i) => {
      const wording = SLIDE_WORDING[String(slides[id] || '')];
      return wording ? `A${i + 1} ${wording}` : '';
    })
    .filter(Boolean)
    .join(', ');
}

/** Sets the M / S labels on a two-leaf drawing so the master leaf matches the chosen side. */
export function applyMasterLeafToPayload<T>(payload: T, masterLeaf: DoorSideCode): T {
  const p: any = payload;
  if (!p || !p.root) return payload;
  const ids = getScheduleOrderedLeafIds(p);
  if (ids.length !== 2) return payload;
  const [leftId, rightId] = ids;
  const pres = (p.presentation && typeof p.presentation === 'object') ? p.presentation : {};
  const sashRoles = { ...(pres.sashRoles || {}), [leftId]: masterLeaf === 'left' ? 'M' : 'S', [rightId]: masterLeaf === 'left' ? 'S' : 'M' };
  return { ...p, presentation: { ...pres, sashRoles } };
}

const PX_PER_MM = 1 / 2.5;
const DOOR_HEIGHT_MM = 2100;

function equalColumnsPayload(count: number, widthMm: number) {
  const leafIds = Array.from({ length: count }, (_, i) => `c${i + 1}`);
  return {
    leafIds,
    payload: {
      root: {
        type: 'rectUnion',
        preset: 'rectangle',
        shapeParams: {},
        regions: [
          {
            id: 'r0',
            x: 0,
            y: 0,
            w: 1,
            h: 1,
            root: {
              id: 'c0',
              split: 'vertical',
              fractions: leafIds.map(() => 1 / count),
              children: leafIds.map((id) => ({ id, split: 'none', children: [], fractions: [] })),
            },
          },
        ],
      },
      w: Math.round(widthMm * PX_PER_MM),
      h: Math.round(DOOR_HEIGHT_MM * PX_PER_MM),
      logicalDivs: {},
    },
  };
}

const doorPresentation = (leafIds: string[], extra: Record<string, unknown> = {}) => ({
  paneHandleKinds: Object.fromEntries(leafIds.map((id) => [id, 'door'])),
  handleStyle: 'upvc',
  doorHandleType: 'lever',
  handleClickMode: 'rotate',
  cillUnionIncludeUpper: false,
  ...extra,
});

/** Two equal leaves hinged on the outside edges, with door handles and M / S labels. */
export function buildFrenchDoorsPayload(masterLeaf: DoorSideCode = DEFAULT_MASTER_LEAF) {
  const { leafIds, payload } = equalColumnsPayload(2, 1500);
  const [left, right] = leafIds;
  return applyMasterLeafToPayload({
    ...payload,
    opens: { [left]: 'left', [right]: 'right' },
    slideOpens: {},
    presentation: doorPresentation(leafIds, { handleDirs: { [left]: 'left', [right]: 'right' } }),
  }, masterLeaf);
}

/** Equal door panels; the traffic door (if any) is hinged on its outside edge. */
export function buildBifoldDoorsPayload(panelCount: number = BIFOLD_PANEL_COUNT_RANGE.default, trafficDoor: TrafficDoorCode = DEFAULT_TRAFFIC_DOOR) {
  const count = clampPanelCount('bifold', panelCount);
  const { leafIds, payload } = equalColumnsPayload(count, count * 800);
  const opens: Record<string, string> = {};
  if (trafficDoor === 'left') opens[leafIds[0]] = 'left';
  if (trafficDoor === 'right') opens[leafIds[count - 1]] = 'right';
  return {
    ...payload,
    opens,
    slideOpens: {},
    presentation: doorPresentation(leafIds, { handleDirs: { ...opens } }),
  };
}

/** Equal panels; the right panel slides left, and with four panels the middle two slide outward. */
export function buildSlidingDoorsPayload(panelCount: number = SLIDER_PANEL_COUNT_RANGE.default) {
  const count = clampPanelCount('slider', panelCount);
  const { leafIds, payload } = equalColumnsPayload(count, count * 900);
  const slideOpens: Record<string, string> = count === 4
    ? { [leafIds[1]]: 'slide-left', [leafIds[2]]: 'slide-right' }
    : { [leafIds[count - 1]]: 'slide-left' };
  return {
    ...payload,
    opens: {},
    slideOpens,
    presentation: doorPresentation(Object.keys(slideOpens)),
  };
}
